import { useEffect, useMemo, useState } from 'react';
import {
  Paper, Stack, Typography, Box, Chip, Tooltip, Link, Divider, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Collapse, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// 사이버보안 점검 대상 커버리지 — "우리가 만드는 SW 가 무엇이고, 점검이 실제로 걸려 있는가".
// 데이터: GET /api/security/scope (cloud-compliance 점검 대장 + Dependency-Track 실측)
//
// 🔴 이 화면 위쪽의 취약점 현황과 **다른 질문**에 답한다.
//   위: Dependabot 이 본 취약점이 몇 건인가 (세는 것)
//   여기: 그걸 세는 **대상**이 맞게 잡혀 있는가 (세는 범위)
//   두 수치가 다른 것이 정상이다 — 도구도 범위도 다르다. 같아 보이게 만들면 거짓말이 된다.
//
// 🔴 가장 중요한 것은 아래쪽 '아직 정하지 못한 것' 이다.
//   귀속 미확인·미등록을 화면에서 빼면 **아무도 다시 묻지 않는다.** 그래서 접어 두되 지우지 않는다.

const C = (theme, s) => ({
  watched: theme.palette.success.main,
  stale: theme.palette.warning.main,
  gap: theme.palette.error.main,
  never: theme.palette.error.main,
}[s] || theme.palette.text.disabled);

export default function ScopeCoverageSection() {
  const theme = useTheme();
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [openUnassigned, setOpenUnassigned] = useState(false);
  const [openProducts, setOpenProducts] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/security/scope');
        const body = await res.json();
        if (!alive) return;
        setState(res.ok
          ? { data: body, error: null, loading: false }
          : { data: null, error: body?.hint || body?.error || `HTTP ${res.status}`, loading: false });
      } catch (e) {
        if (alive) setState({ data: null, error: String(e), loading: false });
      }
    })();
    return () => { alive = false; };
  }, []);

  const { data, error, loading } = state;
  const t = data?.totals || {};
  const legend = data?.legend || {};

  const buckets = useMemo(() => {
    const u = data?.unassigned || [];
    const g = {};
    u.forEach((x) => { (g[x.bucket] ||= []).push(x); });
    return g;
  }, [data]);

  const BUCKET_LABEL = {
    rayteams_candidates: '제품 귀속 미확인',
    shared_libraries: '공통 라이브러리로 보임',
    stale_or_duplicate: '멈췄거나 중복',
    not_registered_anywhere: '🔴 어디에도 등록되지 않음',
  };

  if (loading) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">점검 대상 커버리지</Typography>
        <Typography variant="caption" color="text.secondary">
          우리가 만드는 SW 가 무엇이고, 점검이 실제로 걸려 있는가
        </Typography>
        <Box sx={{ flex: 1 }} />
        {data?.as_of && (
          <Typography variant="caption" color="text.secondary">
            {data.measured_by?.tool} {data.measured_by?.version} · {data.as_of} 기준
          </Typography>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        위의 취약점 현황은 <b>GitHub Dependabot</b>, 여기는 <b>Dependency-Track</b> 기준입니다.
        도구도 범위도 달라 숫자가 다릅니다 — 여기는 <b>세는 대상</b>이 맞는지를 봅니다.
      </Typography>

      {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip label={`제품 ${t.products}`} />
            <Chip label={`코드베이스 ${t.repos}`} />
            {['watched', 'stale', 'never', 'gap'].map((s) => (
              t[s] ? (
                <Tooltip key={s} title={legend.state_why?.[s] || ''}>
                  <Chip
                    label={`${legend.state?.[s] || s} ${t[s]}`}
                    sx={{ bgcolor: 'transparent', border: `1px solid ${C(theme, s)}`, color: C(theme, s) }}
                  />
                </Tooltip>
              ) : null
            ))}
            <Tooltip title={legend.harness_rule || ''}>
              <Chip variant="outlined" label={`🔧 rederi harness ${t.harness_used}`} />
            </Tooltip>
          </Stack>

          {/* 🔴 정해지지 않은 것이 먼저 보여야 한다 — 잘 되고 있는 것보다 중요하다 */}
          {t.unassigned > 0 && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={(
                <Button
                  size="small"
                  onClick={() => setOpenUnassigned((v) => !v)}
                  endIcon={openUnassigned ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {openUnassigned ? '접기' : '보기'}
                </Button>
              )}
            >
              아직 제품을 정하지 못한 코드베이스 <b>{t.unassigned}</b>건이 있습니다 —
              점검은 돌고 있으나 <b>어느 제품의 것인지 모릅니다.</b>
            </Alert>
          )}

          <Collapse in={openUnassigned}>
            <Stack spacing={2} sx={{ mt: 1.5 }}>
              {Object.entries(buckets).map(([k, rows]) => (
                <Box key={k}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {BUCKET_LABEL[k] || k} ({rows.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {rows[0]?.why}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {rows.map((r) => (
                      <Chip
                        key={r.dt_project || r.repo}
                        size="small"
                        variant="outlined"
                        label={r.repo || r.dt_project}
                        color={k === 'not_registered_anywhere' ? 'error' : 'default'}
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Collapse>

          <Divider sx={{ my: 2 }} />

          <Button
            size="small"
            onClick={() => setOpenProducts((v) => !v)}
            endIcon={openProducts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            제품별 보기 ({data.products?.length || 0})
          </Button>

          <Collapse in={openProducts}>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>제품</TableCell>
                  <TableCell>담당</TableCell>
                  <TableCell>식약처</TableCell>
                  <TableCell align="right">코드베이스</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="center">harness</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.products || []).map((p) => (
                  <TableRow key={p.name}>
                    <TableCell>
                      {p.harness?.prd_repo
                        ? <Link href={`/products/${p.harness.prd_repo}`} underline="hover">{p.name}</Link>
                        : p.name}
                      {!p.submitted && (
                        <Tooltip title="신청서가 접수되지 않은 제품입니다">
                          <Chip size="small" color="warning" variant="outlined" label="신청서 없음" sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{p.dev_owner || p.submitted_by || '—'}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{p.regulatory_label || '—'}</Typography></TableCell>
                    <TableCell align="right">{p.repos?.length ?? 0}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {Object.entries(p.counts || {}).map(([s, n]) => (
                          <Typography key={s} variant="caption" sx={{ color: C(theme, s) }}>
                            {(legend.state?.[s] || s).slice(0, 1)}{n}
                          </Typography>
                        ))}
                        {p.shares_codebase_with && (
                          <Typography variant="caption" color="text.secondary">
                            ↳ {p.shares_codebase_with} 와 공유
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {p.harness?.uses_harness === true ? '🔧' : p.harness?.uses_harness === null ? '❓' : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            정본은{' '}
            <Link
              href="https://github.com/rayrndsw/cloud-compliance/blob/main/operations/registers/security-monitoring-scope.yaml"
              target="_blank"
              rel="noreferrer"
              underline="hover"
            >
              점검 대장
            </Link>
            입니다. {data.review?.how}
          </Typography>
        </>
      )}
    </Paper>
  );
}
